import {
  ArrowRight,
  Check,
  CheckCircle2,
  Circle,
  Home,
  Loader2,
  Pencil,
  Tag,
  Thermometer,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  type GatewayDiagnosis,
  getApiErrorMessage,
  type HeatingMappingResult,
  type HeatingZone,
  type RecordIssueRequest,
  type Room,
  useApplyHeatingMapping,
  useGatewayDiagnosis,
  useGateways,
  useHeatingZones,
  useMergeHeatingRooms,
  useProvisionGateway,
  useRecordIssue,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { liveRooms } from "@/lib/rooms";
import { spellCount, spellCountLower } from "@/lib/spell-count";
import { cn } from "@/lib/utils";

import {
  AppBar,
  DiagnosisNotice,
  HelpHint,
  MaterialsNeeded,
  PhaseDots,
  PillButton,
  SkipFailedStep,
  StuckHelp,
  useRetryGate,
} from "./components";
import {
  describeMacMismatchAnswer,
  describeWhatWasTried,
  isEscalation,
  isLinkFault,
  pickGatewayDiagnosis,
  pickNoticeDiagnosis,
} from "./connector-issue";
import {
  EMPTY_MAPPING_DETAIL,
  mappingOutcome,
  roomsToMapLine,
  sheetMayContinue,
} from "./heating-summary";
import { PRODUCT_NAMES } from "./product-names";

const HEATING_CONNECTOR = PRODUCT_NAMES.heatingConnector;

/**
 * Phase 4 · Floor heating (Wavin) — Figma 717:915 (intro), 717:940 (connect),
 * 717:970 (connecting), 717:860 (review mapping), 717:1085 (fire intro),
 * 717:1119 (raise thermostat), 717:1138 (listening), 717:1180 (circuit
 * detected), 717:1044 (all mapped).
 *
 * Real wiring: "It's plugged in" provisions the Wavin gateway on the hub
 * (usr_provision, MAC from the household's preconfig); the connecting stage
 * polls until the gateway entry loads and the hub reports its heating zones.
 * Circuits ARE those zones. The room-by-room path watches live valve state:
 * the user raises a thermostat and the zone whose valve opens is the one
 * they're standing next to. Finishing pushes the circuit→room mapping to the
 * hub (areas + entity renames) and stores it on the household.
 *
 * Connecting leads straight to `review`, the editable circuit list. There is
 * no method-choice screen and no photo of the printed list: both existed once
 * (Figma 717:1011, 717:820, 717:841) and were cut in #72. Asking someone which
 * of two mapping methods they wanted, before they knew what either involved,
 * was a decision they had no basis to make. The list is now the destination,
 * and the room-by-room walk is a link on it for anyone whose Wavin lid is
 * blank. Don't reintroduce the scan: it only ever pre-seeded a list the user
 * had to confirm by hand anyway, so it added a step without removing one.
 */

const FALLBACK_ROOMS: Room[] = [
  { name: "Living room", area_m2: 28 },
  { name: "Kitchen", area_m2: 14 },
  { name: "Hallway", area_m2: 6 },
  { name: "Master bedroom", area_m2: 16 },
  { name: "Bathroom", area_m2: 8 },
];

/** Poll cadence for the gateway entry and live zone/valve state. */
const POLL_MS = 3000;

/**
 * How long the connect may run before we offer the assistant.
 *
 * Not a timeout and not a warning. Provisioning a USR gateway legitimately runs
 * to fifteen minutes, so anything that reads as "this has failed" would be
 * wrong most times it fired. Five minutes is past the common case and well
 * short of the ceiling, which makes it the point where a resident wants
 * something to do other than reach for the plug.
 */
const CONNECT_PATIENCE_MS = 5 * 60 * 1000;

/**
 * Automatic retries of a failed provision before the background diagnosis
 * starts. Not a ceiling on retrying itself — see the retry effect below,
 * which keeps going past this — only on when the diagnosis call fires and
 * how the eventual issue report describes the attempt count.
 */
const DIAGNOSIS_THRESHOLD = 3;

/**
 * Backoff between automatic provision retries, so a connector that is never
 * going to register does not get hammered with requests while the resident
 * watches the checklist spin.
 */
const PROVISION_RETRY_DELAY_MS = 5000;

/**
 * How long the connector must stay confirmedly failing before the gentle
 * "Still waiting?" prompt escalates to "We've noticed an issue". Past
 * DIAGNOSIS_THRESHOLD's own worst case (three retries at
 * PROVISION_RETRY_DELAY_MS apiece), so the button offering the diagnosis
 * never appears before that diagnosis has had a chance to come back.
 */
const CONNECT_FAILED_WINDOW_MS = 20 * 1000;

/**
 * How long the zone list may stay empty before we call it "no circuits".
 *
 * The Wavin integration reads its hardware on a thirty-second interval, so the
 * first successful fetch after the gateway loads can legitimately come back
 * empty while that first read is still in flight. Declaring failure then would
 * dead-end a house that is about to work, so the verdict waits until the list
 * has stayed empty past that interval. `connecting` keeps polling meanwhile,
 * which is the point: `no-circuits` is outside `zonesActive`, so arriving there
 * early would also stop the polling that would have proved us wrong.
 */
const ZONES_GRACE_MS = 45 * 1000;

/**
 * How long the connect may run before the skip appears.
 *
 * The escape hatch for a connector the hub will never discover, and the person
 * waiting is usually standing in someone's home with the rest of the install
 * still to do. Offering the way out costs a resident nothing (the CTA is quiet,
 * and the spinner keeps going) and saves an installer minutes of watching a
 * spinner they already know is stuck.
 *
 * Derived rather than fixed, because it must never arrive BEFORE the offer of
 * help. Confirming silent circuits takes ZONES_GRACE_MS to notice plus
 * CONNECT_FAILED_WINDOW_MS to confirm, and at 60 s flat the skip beat that by
 * five seconds: the resident was invited to give up before anything offered to
 * help them. Deriving it means changing either gate cannot reopen that gap.
 */
const SKIP_PATIENCE_MS = ZONES_GRACE_MS + CONNECT_FAILED_WINDOW_MS + 15 * 1000;

/** Setpoint rise (°C) vs. baseline that identifies a raised thermostat. */
const SETPOINT_RAISE_C = 2;

/**
 * After the first circuit fires, keep listening this long for siblings: one
 * wall remote often drives several circuits, and their setpoints arrive in
 * the same integration refresh (the Wavin updates HA every ~30-60s).
 */
const HIT_GRACE_MS = 8000;

/** Per-zone snapshot taken before the user walks off to raise a thermostat. */
interface ZoneBaseline {
  valve: boolean;
  target: number | null;
  action: string | null;
}

/** Heating's flat module block — brand Background Red. See the tint map in
 *  globals.css; the same colour marks this module on the guide. */
const MODULE_HERO = "bg-tint-rose";

/** Wavin discovery steps shown while the gateway is provisioned and comes up. */
const CONNECT_STEPS = [
  "Heating Connector registered",
  "Verifying connection",
  "Reading heating zones",
];

interface Circuit {
  /** The thermostat's zone number: the lowest circuit in its group. */
  id: number;
  /** Every circuit the thermostat drives, the zone itself included. */
  circuits: number[];
}

/**
 * Sentinel room for a thermostat that heats a room this setup does not cover.
 *
 * Records hold only the rooms in the pilot, so a hallway or a spare bedroom has
 * no destination in the handoff. The resident still needs a way to say "that
 * one is real, it is just not one of these": leaving it on "Not connected"
 * reads as a broken circuit, and free text would create a room nobody consumes.
 * "Not tracking" is the word on purpose: the room keeps heating exactly as
 * before, SmartClimate simply does not manage it. Never sent to the mapping
 * call; travels in the step payload for support.
 */
/** One thermostat whose circuits the resident placed in two or more record rooms. */
interface SharedThermostat {
  zone: number;
  circuits: number[];
  /** Record rooms, lowest circuit first: the first holds the thermostat and survives. */
  rooms: string[];
}

const OUTSIDE_PILOT = "__outside__";
const OUTSIDE_PILOT_LABEL = "A room we're not tracking";

/** Circuit numbers as prose: "4, 7 and 8". Numbers here are addresses, so they stay digits. */
function joinNumbers(nums: number[]): string {
  const sorted = [...nums].sort((a, b) => a - b);
  if (sorted.length <= 1) {
    return sorted.join("");
  }
  return `${sorted.slice(0, -1).join(", ")} and ${sorted[sorted.length - 1]}`;
}

/**
 * Is this circuit actually telling us anything?
 *
 * The zone list comes from the entity registry, which exists whether or not
 * the Wavin is answering, so a list of sixteen circuits proves nothing on its
 * own. With the RS-485 cable out of the connector the hub still reports all
 * sixteen, every one of them with no reading at all, and the checklist used to
 * tick green and tell the household their Wavin was connected. They would then
 * map their whole house against circuits that cannot reply.
 *
 * Any one signal is enough. A circuit with an actuator but no thermostat can
 * legitimately report no temperature while its setpoint still comes through,
 * so requiring a temperature specifically would fail a working house.
 * `valve_open` is deliberately not consulted: it is derived from a switch
 * state, so it reads `false` rather than absent when the switch is
 * unavailable, which makes it useless as a sign of life.
 */
function zoneHasReading(zone: HeatingZone): boolean {
  return (
    zone.current_temperature != null || zone.target_temperature != null || zone.hvac_action != null
  );
}

/**
 * Every stage of this module, in order — the dev screen gallery walks it, and
 * `Stage` is derived from it so the two can never disagree. Add a stage here
 * and it is in the gallery; there is no second list to remember.
 */
export const STAGES = [
  "intro",
  "connect",
  "connecting",
  // The hub is provisioned for a different device than the one on our
  // record (VLX-111). Its own stage, not a special case bolted onto
  // "connecting": there is a photo to show and a question only the
  // household can answer, and neither fits the checklist layout.
  "mac-mismatch",
  // The hub answered but the Wavin reported no circuits. Its own stage rather
  // than an error on the list, because there is nothing to map and no amount of
  // retrying from this screen will conjure a circuit.
  "no-circuits",
  // The list IS the mapping screen, reached straight after connecting. Danish
  // homes must have the circuit list printed on the Wavin, so most households
  // read it off and are done. The room-by-room walk below is the fallback for
  // anyone who cannot, reached from a link on the list.
  "review",
  // One thermostat, two record rooms: confirm before they become one.
  "confirm-merge",
  "fire-intro",
  "fire-raised",
  "fire-listening",
  "fire-detected",
  "done",
] as const;

export type Stage = (typeof STAGES)[number];

interface FloorHeatingScreenProps {
  rooms: Room[];
  busy: boolean;
  error: string;
  /** True while the skip is in flight, so its button can't be double-tapped. */
  skipping?: boolean;
  /** Records this compartment as skipped, carrying the error that caused it. */
  onSkip?: (reason: string) => void;
  onComplete: (payload: Record<string, unknown>) => void;
  /**
   * Stage to open on. Production always omits it (each module starts at its
   * own first stage); the dev screen gallery uses it to deep-link a stage.
   */
  initialStage?: Stage;
  /**
   * A stand-in verdict for the dev screen gallery, so both notices on the
   * connecting stage can be reviewed without a broken connector to hand.
   * Production always omits it: the real verdict comes from the background
   * diagnosis, and this one deliberately outranks it so the gallery is not at
   * the mercy of whatever the office hub is doing.
   */
  previewDiagnosis?: GatewayDiagnosis;
  /**
   * Circuits for the design gallery, which has no hub to ask. Mirrors
   * `previewCovers` on the windows screen. Never set in the real flow: a made
   * up circuit list is exactly what this screen refuses to show a resident.
   */
  previewCircuits?: HeatingZone[];
  /** Gallery only: shared thermostats to show on the confirm screen without a hub. */
  previewSharedThermostats?: SharedThermostat[];
}

export function FloorHeatingScreen({
  rooms,
  busy,
  error,
  skipping = false,
  onSkip,
  onComplete,
  initialStage,
  previewDiagnosis,
  previewCircuits,
  previewSharedThermostats,
}: FloorHeatingScreenProps) {
  const roomNames = useMemo(() => {
    const live = liveRooms(rooms);
    const source = live.length > 0 ? live : FALLBACK_ROOMS;
    return source.map((room) => room.name);
  }, [rooms]);
  // The record's own rooms, without the display fallback above: whether an
  // empty sheet may hand in depends on rooms the house really has, and so does
  // the line that names them. A house with no rooms on record must not be told
  // to go and map a Kitchen it never registered.
  const recordRoomNames = useMemo(() => liveRooms(rooms).map((room) => room.name), [rooms]);
  const pilotRoomCount = recordRoomNames.length;

  const [stage, setStage] = useState<Stage>(initialStage ?? "intro");
  // Circuit number → room name (a record room, OUTSIDE_PILOT, or absent when
  // untouched). Keyed by circuit, not thermostat: the resident maps every
  // circuit, and a thermostat whose circuits land in two record rooms is the
  // open-plan merge case, which only an explicit per-circuit answer can reveal.
  const [mapping, setMapping] = useState<Record<number, string>>({});
  // Shared thermostats still to be confirmed as merges, first one on screen.
  const [mergeQueue, setMergeQueue] = useState<SharedThermostat[]>(
    () => previewSharedThermostats ?? [],
  );
  const [mergeError, setMergeError] = useState("");
  const mergeRooms = useMergeHeatingRooms();
  // An emptied queue on the confirm screen means every merge is done. The
  // transition lives in an effect, not in render, where a state update is a
  // side effect React may run twice.
  useEffect(() => {
    if (stage === "confirm-merge" && mergeQueue.length === 0) {
      setStage("done");
    }
  }, [stage, mergeQueue.length]);
  // Room-by-room path state: which room we're visiting, the circuits that
  // fired in this listening window, the zones already confirmed, the room the
  // detected set will be assigned to, and the signal baseline captured before
  // the user walks off.
  const [roomIdx, setRoomIdx] = useState(0);
  const [pendingHits, setPendingHits] = useState<number[]>([]);
  const [confirmed, setConfirmed] = useState<number[]>([]);
  const [detectedRoom, setDetectedRoom] = useState("");
  const [baseline, setBaseline] = useState<Record<number, ZoneBaseline> | null>(null);
  // Whether authoritative setpoint evidence has appeared in this listening
  // window — see the detection effect.
  const [sawSetpointHit, setSawSetpointHit] = useState(false);
  // When the gateway first reported loaded, so an empty zone list can be given
  // ZONES_GRACE_MS before it counts as "none".
  const [gatewayLoadedAt, setGatewayLoadedAt] = useState<number | null>(null);
  // Circuits are listed but not one of them is answering, past the same grace
  // window an empty list gets. State rather than a derived value because the
  // diagnosis query below is declared before the zone query it depends on, and
  // because it must latch: a later poll that comes back just as empty should
  // not reset the clock. Set by the silent-circuits effect.
  const [circuitsSilent, setCircuitsSilent] = useState(false);
  // The bridge is up and the integration behind it has no entry at all, past
  // the same grace window. A separate latch from `circuitsSilent` because the
  // two are opposite facts: there, circuits are listed and stay quiet; here
  // there is nothing to list because the setup never ran. Reporting this as
  // "no circuits" blamed the manifold for a fault of ours, and sent two beta
  // houses looking for a loose cable that was never loose (VLX-116).
  const [integrationMissing, setIntegrationMissing] = useState(false);
  const [applyError, setApplyError] = useState("");
  // A mapping already pushed to the hub, kept so acknowledging unmatched
  // circuits doesn't re-apply it.
  const [appliedResult, setAppliedResult] = useState<HeatingMappingResult | null>(null);
  // Every provision attempt that has failed since the last tap of "The power
  // light is on" (or the last success). Latched rather than derived from the
  // mutation's own isError, which flips false the instant a retry goes
  // pending — see the retry effect below.
  const [failedAttempts, setFailedAttempts] = useState(0);
  // Snapshot of the diagnosis that sent us to "mac-mismatch", taken at the
  // moment of the transition. `activeDiagnosis` itself goes undefined the
  // instant `stage` stops being "connecting" (its query is only enabled on
  // that stage), so the mismatch screen and the issue it records both read
  // this snapshot rather than the live (by then empty) value.
  const [mismatchDiagnosis, setMismatchDiagnosis] = useState<GatewayDiagnosis | undefined>(
    undefined,
  );

  const provision = useProvisionGateway();
  const applyMapping = useApplyHeatingMapping();
  const recordIssue = useRecordIssue();

  const provisionOk =
    provision.data != null &&
    (provision.data.status === "created" || provision.data.status === "already_configured");
  const provisionFailed =
    provision.isError || (provision.data != null && provision.data.status === "error");

  const gateways = useGateways({
    enabled: stage === "connecting" && provisionOk,
    refetchInterval: POLL_MS,
  });
  // The gateway WE just provisioned, by MAC, not merely one whose title
  // mentions "wavin": a MAC change leaves the old entry behind, still loaded,
  // so matching on title alone reports success for a connector that was never
  // found (VLX-111). `provision.data` always carries the MAC by the time this
  // query runs, since `provisionOk` requires it.
  const entry = gateways.data?.find((e) => e.mac === provision.data?.mac);
  const gatewayLoaded = entry?.state === "loaded";
  // A different fact from `gatewayLoaded`, and deliberately not folded into it.
  // `state` says usr_provision found the bridge; `target_state` says the
  // integration that actually reads the manifold was set up behind it. Folding
  // the second into the first looks tempting and breaks this screen: every
  // timer here hangs off `gatewayLoaded`, so a false reading there stops the
  // zone poll, and with it the grace window, the diagnosis and the offer of
  // help. `target_domain` is null for a YAML target (Nilan), which has no entry
  // of its own, so that case is never treated as missing.
  //
  // Null specifically, not merely "not loaded": null means there is no entry at
  // all, which is the fault here. A string means an entry exists and reached
  // that state, and `setup_retry` behind a working bridge is a different fault
  // that the diagnosis already reads out of entities and logs. Calling that one
  // missing would be as wrong as the message this replaces.
  const targetEntryMissing = !!entry?.target_domain && entry.target_state == null;

  const zonesActive =
    (stage === "connecting" && gatewayLoaded) ||
    stage === "fire-raised" ||
    stage === "fire-listening";
  const zones = useHeatingZones({
    enabled: zonesActive,
    refetchInterval: zonesActive ? POLL_MS : undefined,
  });

  // True for the whole stretch a connect attempt is failing, not just the
  // instant the mutation reports an error — see `failedAttempts` above.
  //
  // Silent circuits count as failing even though nothing errored. Registering
  // the connector says only that the bridge is on the network; it says nothing
  // about the RS-485 side behind it, so a cable out of its green socket leaves
  // every call succeeding while no data moves at all. Without this the screen
  // would sit on a spinner with no way out, since the offer of help and the
  // skip both hang off this flag.
  const isConnectFailing =
    stage === "connecting" &&
    ((failedAttempts > 0 && !provisionOk) || circuitsSilent || integrationMissing);
  // Runs once, in the background, as soon as retrying alone stops looking
  // like enough — well before anything about it reaches the resident, so the
  // verdict already exists by the time the "We've noticed an issue" button
  // or a skip needs it.
  //
  // Two different questions get asked here. After repeated failures it is "why
  // will this not register?". On an apparently clean connect whose circuits are
  // all silent it is "is anything actually flowing?", which nothing else on
  // this screen can answer: provisioning succeeded, the entry is loaded, and
  // the zone list is full.
  const diagnosisEligible =
    stage === "connecting" &&
    ((failedAttempts >= DIAGNOSIS_THRESHOLD && !provisionOk) ||
      circuitsSilent ||
      integrationMissing);
  const diagnosis = useGatewayDiagnosis({ enabled: diagnosisEligible });
  // Gated on the same eligibility the query itself uses, not just on
  // `provisionOk`: a query's cached data outlives `enabled` flipping off, so
  // a fresh attempt that hasn't failed three times yet must not show or
  // record the verdict from an earlier, already-abandoned run.
  const activeDiagnosis = previewDiagnosis
    ? previewDiagnosis
    : diagnosisEligible
      ? pickGatewayDiagnosis(diagnosis.data, "wavin")
      : undefined;
  // Confirms a raw "is it failing" signal into "has this been failing for a
  // while", the same gate hub-setup-screen uses for its own stuck-help offer.
  const retryState = useRetryGate(isConnectFailing, CONNECT_FAILED_WINDOW_MS);
  // The verdict to put on screen, and whether it is one nobody in the house
  // can act on. Both live here rather than at the call site so the connecting
  // stage reads as one decision instead of three nested conditions.
  const noticeDiagnosis = pickNoticeDiagnosis(activeDiagnosis);
  const escalating = isEscalation(activeDiagnosis);

  /**
   * Every issue this screen files, behind one gate.
   *
   * Fire-and-forget: a failure here must never block the skip it rides along
   * with. Silent in the dev gallery, which drives the diagnosis stages from a
   * fixture verdict rather than from hardware, so a designer clicking through
   * "Continue with the rest" to see the notice's CTA does not leave real rows
   * on a real household's report.
   */
  function reportIssue(body: RecordIssueRequest) {
    if (previewDiagnosis != null) {
      logger.debug("[onboarding] preview mode, issue not recorded", body);
      return;
    }
    recordIssue.mutate(body, {
      onError: (err) => logger.warn("[onboarding] could not record the connector issue", err),
    });
  }

  /** Records this connector giving up, so the end-of-onboarding report knows
   *  what was tried. */
  function recordConnectorIssue(stageName: string, reason: string) {
    reportIssue({
      step_key: "floor_heating",
      stage: stageName,
      verdict: activeDiagnosis?.verdict,
      action: activeDiagnosis?.action,
      detail: activeDiagnosis?.detail ?? reason,
      // The diagnosis's supporting observations and the connector's address
      // are for the support report, never for the resident: the email's
      // evidence block is only as good as what is stored here.
      findings: activeDiagnosis?.findings ?? [],
      connector_address: activeDiagnosis?.address ?? undefined,
      // +1: `failedAttempts` counts retries dispatched, so the first tap
      // itself is one more try than that.
      what_was_tried: describeWhatWasTried(failedAttempts + 1, activeDiagnosis),
    });
  }

  /**
   * Record what went wrong on the connecting stage, then carry the household
   * past this compartment.
   *
   * One function behind two buttons on purpose. The timed skip and the escalate
   * notice's "Continue with the rest" do exactly the same thing; the only
   * difference is that the notice knows the verdict already, so it does not
   * make anyone sit out SKIP_PATIENCE_MS first to reach a conclusion we have
   * already drawn.
   */
  function continueWithoutConnector() {
    const reason =
      noticeDiagnosis?.detail ?? entry?.reason ?? `The ${HEATING_CONNECTOR} never came online.`;
    recordConnectorIssue("connecting", reason);
    onSkip?.(reason);
  }

  /**
   * Give up on a mapping the hub refused to store, and say so in the report.
   *
   * A different failure from every other one on this screen, and it used to be
   * the only skip that recorded nothing: the connector is connected, the hub
   * answered, and the write itself was rejected. Support would have seen a
   * compartment marked skipped with no issue beside it, which reads as a
   * resident who chose to move on rather than one the hub turned away. Its own
   * verdict string keeps the upsert key clear of the connecting stage's report.
   */
  function skipUnsavedMapping() {
    reportIssue({
      step_key: "floor_heating",
      stage: "review",
      verdict: "mapping_not_saved",
      detail: applyError,
      what_was_tried:
        "The circuits were matched to rooms by hand. Saving that mapping to the hub failed.",
    });
    onSkip?.(applyError);
  }

  /**
   * The resident hands in an empty sheet and chooses to move on anyway.
   *
   * Recorded as its own verdict rather than riding the unsaved-mapping one:
   * nothing was saved because nothing was attempted, and support reading
   * "could not be saved" would go looking for a hub fault that is not there.
   * The skip flags the house for support, which is the right outcome for a
   * heating step that finished with no heating in it (Beta Test House 4).
   */
  function skipEmptyMapping() {
    const detail = EMPTY_MAPPING_DETAIL(unassignedCount, allCircuits.length, pilotRoomCount);
    const untouched =
      unassignedCount === allCircuits.length
        ? "every circuit was left on Not connected"
        : `${unassignedCount} of ${allCircuits.length} circuits were left on Not connected and the rest were marked as not tracking`;
    reportIssue({
      step_key: "floor_heating",
      stage: "review",
      verdict: "mapping_empty",
      detail,
      what_was_tried: `The circuit sheet was opened and ${untouched}. The resident chose to move on without mapping.`,
    });
    onSkip?.(detail);
  }

  /**
   * Record which way the household answered the mac-mismatch question, then
   * move on. Neither answer is ours to act on directly (VLX-111 explains why:
   * three real situations produce this same finding and need opposite fixes),
   * so both ride the same skip path the rest of this screen uses to carry the
   * household past a compartment that cannot succeed from here.
   */
  function answerMacMismatch(matches: boolean) {
    reportIssue({
      step_key: "floor_heating",
      stage: "mac_mismatch",
      verdict: mismatchDiagnosis?.verdict,
      action: mismatchDiagnosis?.action,
      detail: mismatchDiagnosis?.detail,
      findings: mismatchDiagnosis?.findings ?? [],
      connector_address: mismatchDiagnosis?.address ?? undefined,
      what_was_tried: `${describeWhatWasTried(failedAttempts + 1, mismatchDiagnosis)} ${describeMacMismatchAnswer(matches)}`,
    });
    onSkip?.(
      mismatchDiagnosis?.detail ??
        "The hub is provisioned for a different device than the one on record.",
    );
  }

  // Circuits are the hub's real zones, and ONLY those. This used to fall back to
  // the sixteen a Wavin can carry whenever the hub reported none, which read as
  // a working screen and was anything but: every row was invented, so the save
  // matched nothing and rejected the lot after the resident had mapped their
  // whole house. A hub reporting no zones is a real condition with its own
  // stage ("no-circuits") — it is not something to paper over with a list.
  // A circuit is unassigned ("") until the user deliberately maps it.
  const zoneData = previewCircuits ?? zones.data;
  const zoneIds = zoneData?.map((z) => z.zone) ?? [];
  const circuits: Circuit[] = (zoneData ?? []).map((z) => ({
    id: z.zone,
    circuits: z.circuits?.length ? z.circuits : [z.zone],
  }));
  const hasGroups = circuits.some((c) => c.circuits.length > 1);
  const allCircuits = circuits.flatMap((c) => c.circuits);
  /** Where a circuit was placed: a record room, OUTSIDE_PILOT, or "" when untouched. */
  const roomOf = (circuit: number) => mapping[circuit] ?? "";
  /** The record rooms a thermostat's circuits were placed in, lowest circuit first, no repeats. */
  const zoneRooms = (c: Circuit) =>
    c.circuits
      .map(roomOf)
      .filter((room, i, all) => room && room !== OUTSIDE_PILOT && all.indexOf(room) === i);
  /** The room a thermostat maps to on the hub: its lowest circuit placed in a record room. */
  const zoneRoom = (c: Circuit): string | undefined => zoneRooms(c)[0];

  function assignRoom(circuit: number, room: string) {
    setMapping((prev) => {
      const next = { ...prev };
      if (room) {
        next[circuit] = room;
      } else {
        delete next[circuit];
      }
      return next;
    });
  }

  // Circuits that are listed AND answering. A list alone is not evidence: see
  // `zoneHasReading`.
  const zonesRead = zones.isSuccess && (zones.data ?? []).some(zoneHasReading);
  // `isLinkFault` is a second, independent guard on the same class of mistake:
  // the hub's own verdict on whether data is moving. It fails open, so a
  // diagnosis that cannot be reached never blocks a household.
  const linkFault = isLinkFault(activeDiagnosis);
  const connectStep = !provisionOk ? 0 : !gatewayLoaded ? 1 : zonesRead && !linkFault ? 3 : 2;
  // All three checklist items green. The household watched that happen, so
  // it waits for their own tap of Continue rather than moving on for them.
  const readyToContinue = connectStep >= CONNECT_STEPS.length;

  // Retry a failed provision automatically instead of bouncing the resident
  // back to an error screen: a USR gateway often needs more than one attempt,
  // and every retry happens while the checklist keeps spinning. The count
  // only gates the background diagnosis and the "We've noticed an issue"
  // button (via `retryState` above) — it never stops the retries themselves.
  useEffect(() => {
    if (stage !== "connecting" || !provisionFailed) {
      return;
    }
    const timer = setTimeout(() => {
      setFailedAttempts((n) => n + 1);
      provision.mutate({ kind: "wavin" });
    }, PROVISION_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
    // `provision` is a stable mutation object; depending on it would re-fire
    // this effect on every state change instead of only on a new failure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, provisionFailed]);

  // Connecting: the diagnosis says our record and the hub disagree on which
  // device this is. That is not something retrying fixes, so it gets its own
  // stage rather than leaving the checklist spinning on a gateway that will
  // never load (VLX-111). The verdict is snapshotted here because leaving
  // "connecting" disables the diagnosis query behind it.
  useEffect(() => {
    if (stage !== "connecting" || activeDiagnosis?.verdict !== "mac_mismatch") {
      return;
    }
    setMismatchDiagnosis(activeDiagnosis);
    setStage("mac-mismatch");
  }, [stage, activeDiagnosis]);

  // Connecting: an empty zone list past the grace window is a settled
  // negative answer ("no-circuits"), not a success the household watched, so
  // it still moves on by itself. A non-empty read IS a success they watched —
  // see the Continue button on this stage, which is what advances from there.
  useEffect(() => {
    if (
      stage !== "connecting" ||
      provisionFailed ||
      !provisionOk ||
      !gatewayLoaded ||
      !zones.isSuccess ||
      (zones.data?.length ?? 0) > 0
    ) {
      return;
    }
    const settled = gatewayLoadedAt !== null && Date.now() - gatewayLoadedAt >= ZONES_GRACE_MS;
    if (!settled) {
      // Stay on `connecting`, which is what keeps the poll running.
      return;
    }
    if (targetEntryMissing) {
      // An empty list has two causes and they need opposite answers. With no
      // entry behind the bridge there is nothing that could have reported a
      // circuit yet, so "no circuits found" would blame the manifold for a
      // setup that never ran. Hand it to the fault path, which asks the
      // diagnosis and gets `setup_failed` back, and stay on `connecting` so
      // the poll keeps running in case the entry appears.
      logger.warn("[onboarding] bridge is up but its integration has no entry");
      setIntegrationMissing(true);
      return;
    }
    logger.warn("[onboarding] wavin gateway loaded but reported no circuits");
    setStage("no-circuits");
    // `dataUpdatedAt` rather than `data`: structural sharing keeps the array
    // reference stable across identical polls, so without it the effect would
    // never re-run to notice the grace window had passed.
  }, [
    stage,
    provisionFailed,
    provisionOk,
    gatewayLoaded,
    gatewayLoadedAt,
    zones.isSuccess,
    zones.data,
    zones.dataUpdatedAt,
    targetEntryMissing,
  ]);

  // Connecting: circuits are listed but none of them is answering. Distinct
  // from `no-circuits`, which is an empty list and means the Wavin genuinely
  // controls nothing. Here the Wavin controls plenty and we cannot hear any of
  // it, which is a fault to report rather than a house to configure. It gets
  // the same grace window as an empty list, because the first read after the
  // gateway loads can legitimately land before any circuit has been polled.
  useEffect(() => {
    if (stage !== "connecting") {
      if (circuitsSilent) {
        setCircuitsSilent(false);
      }
      if (integrationMissing) {
        setIntegrationMissing(false);
      }
      return;
    }
    if (integrationMissing && !targetEntryMissing) {
      // The entry showed up on a later pass, so the earlier verdict is stale.
      // Checked before the empty-list guard below, because an empty list is
      // exactly the state this latch was set in.
      setIntegrationMissing(false);
      return;
    }
    if (!provisionOk || !gatewayLoaded || !zones.isSuccess || (zones.data?.length ?? 0) === 0) {
      return;
    }
    if (zonesRead) {
      // Something answered, so an earlier verdict of silence was premature.
      if (circuitsSilent) {
        setCircuitsSilent(false);
      }
      return;
    }
    const settled = gatewayLoadedAt !== null && Date.now() - gatewayLoadedAt >= ZONES_GRACE_MS;
    if (settled && !circuitsSilent) {
      logger.warn("[onboarding] wavin listed circuits but none of them is reporting");
      setCircuitsSilent(true);
    }
    // `dataUpdatedAt` for the same reason the no-circuits effect needs it:
    // structural sharing keeps the array reference stable across identical
    // polls, so without it this would never re-run to notice the window pass.
  }, [
    stage,
    provisionOk,
    gatewayLoaded,
    gatewayLoadedAt,
    zones.isSuccess,
    zones.data,
    zones.dataUpdatedAt,
    zonesRead,
    circuitsSilent,
    integrationMissing,
    targetEntryMissing,
  ]);

  // Stamp when the CURRENT attempt saw the gateway come up, and clear it
  // whenever that attempt ends. Keeping one stamp for the life of the screen
  // meant a retry inherited an expired clock: the gateway reloads, the first
  // poll is legitimately empty, and the already-elapsed window sent it straight
  // to `no-circuits` — the exact bug the window exists to prevent.
  useEffect(() => {
    if (stage === "connecting" && gatewayLoaded) {
      if (gatewayLoadedAt === null) {
        setGatewayLoadedAt(Date.now());
      }
    } else if (gatewayLoadedAt !== null) {
      setGatewayLoadedAt(null);
    }
  }, [stage, gatewayLoaded, gatewayLoadedAt]);

  // Snapshot every zone's signals when the user is told to go raise a
  // thermostat, so any later change stands out against it.
  useEffect(() => {
    if (stage === "fire-raised" && baseline === null && zones.data) {
      setBaseline(
        Object.fromEntries(
          zones.data.map((z) => [
            z.zone,
            {
              valve: z.valve_open === true,
              target: z.target_temperature ?? null,
              action: z.hvac_action ?? null,
            } satisfies ZoneBaseline,
          ]),
        ),
      );
    }
  }, [stage, baseline, zones.data]);

  // Real circuit detection — a zone counts as "fired" on whichever signal
  // this integration version exposes: the wall thermostat's setpoint rising,
  // hvac_action flipping to heating, or the zone valve opening. Hits are
  // COLLECTED (one remote can drive several circuits) rather than ending the
  // listening immediately.
  useEffect(() => {
    if (stage !== "fire-listening" || !zones.data || baseline === null) {
      return;
    }
    const candidates = zones.data.filter(
      (z) =>
        !confirmed.includes(z.zone) && !pendingHits.includes(z.zone) && baseline[z.zone] != null,
    );
    // A raised setpoint can only come from a person standing at the thermostat,
    // so it is authoritative. Heating starting and valves opening are
    // CONSEQUENCES the Wavin also produces on its own schedule — attributing
    // one of those to whichever room the resident happens to be in would
    // silently mis-map a circuit. So setpoint evidence wins, and once any has
    // been seen in this window the inferred signals are ignored for the rest
    // of it (they'd otherwise sneak in on a later poll).
    const raised = candidates.filter((z) => {
      const base = baseline[z.zone]!;
      return (
        z.target_temperature != null &&
        base.target != null &&
        z.target_temperature - base.target >= SETPOINT_RAISE_C
      );
    });
    if (raised.length > 0) {
      logger.debug("[onboarding] circuits fired (setpoint)", raised);
      setSawSetpointHit(true);
      setPendingHits((prev) => [...prev, ...raised.map((z) => z.zone)]);
      return;
    }
    if (sawSetpointHit) {
      return;
    }
    const inferred = candidates.filter((z) => {
      const base = baseline[z.zone]!;
      return (
        (z.hvac_action === "heating" && base.action !== "heating") ||
        (z.valve_open === true && !base.valve)
      );
    });
    if (inferred.length > 0) {
      logger.debug("[onboarding] circuits fired (heating/valve)", inferred);
      setPendingHits((prev) => [...prev, ...inferred.map((z) => z.zone)]);
    }
  }, [stage, zones.data, baseline, confirmed, pendingHits, sawSetpointHit]);

  // Once something fired, give sibling circuits a grace window to show up,
  // then present the whole set for confirmation.
  const hasHits = pendingHits.length > 0;
  useEffect(() => {
    if (stage !== "fire-listening" || !hasHits) {
      return;
    }
    const timer = setTimeout(() => {
      setDetectedRoom(roomNames[roomIdx] ?? roomNames[0]!);
      setStage("fire-detected");
    }, HIT_GRACE_MS);
    return () => clearTimeout(timer);
  }, [stage, hasHits, roomNames, roomIdx]);

  function startConnecting() {
    setFailedAttempts(0);
    provision.mutate({ kind: "wavin" });
    setStage("connecting");
  }

  /** Confirm the detected circuit set for a room, then visit the next room. */
  function confirmDetected() {
    const nextConfirmed = [...confirmed, ...pendingHits];
    setMapping((prev) => ({
      ...prev,
      ...Object.fromEntries(
        pendingHits.flatMap((zone) =>
          (circuits.find((c) => c.id === zone)?.circuits ?? [zone]).map((n) => [n, detectedRoom]),
        ),
      ),
    }));
    setConfirmed(nextConfirmed);
    // Pass the fresh count: `confirmed` is still the pre-update value here.
    advanceRoom(nextConfirmed.length);
  }

  /** Move to the next room (or the summary), resetting the listening state. */
  function advanceRoom(confirmedCount: number = confirmed.length) {
    setPendingHits([]);
    setBaseline(null);
    setSawSetpointHit(false);
    // Finish when every discovered circuit is mapped, not only when the room
    // list runs out: a house with more rooms than circuits would otherwise
    // march the resident through rooms that cannot have anything left to find.
    const allCircuitsMapped = zoneIds.length > 0 && confirmedCount >= zoneIds.length;
    if (allCircuitsMapped || roomIdx + 1 >= roomNames.length) {
      setStage("review");
    } else {
      setRoomIdx((n) => n + 1);
      setStage("fire-raised");
    }
  }

  // Only zones actually assigned get mapped — spare/unconnected circuits stay
  // unmapped by design ("map the rooms, ignore the rest").
  // The hub takes one room per thermostat: the room of its lowest circuit that
  // sits in a record room. Other rooms in the same group are the merge case,
  // reported below rather than silently dropped.
  const assigned = circuits.flatMap((c) => {
    const room = zoneRoom(c);
    return room ? [{ zone: c.id, circuits: c.circuits, room }] : [];
  });
  // Per circuit, for the summary: a group can mix one circuit placed outside
  // the pilot with siblings left unmapped, and only the former is "not tracked".
  const outsidePilotCircuits = allCircuits.filter((n) => roomOf(n) === OUTSIDE_PILOT);
  const unassignedCount = allCircuits.filter((n) => !roomOf(n)).length;
  // Whether the summary may hand this sheet in at all. See heating-summary.ts.
  const outcome = mappingOutcome({
    assigned: assigned.length,
    unassigned: unassignedCount,
    pilotRooms: pilotRoomCount,
  });
  const mayContinue = sheetMayContinue(outcome);
  // Which of the record's rooms still have no circuit, named, for the line at
  // the top of the sheet.
  // Every room named anywhere on the sheet, not one per thermostat. A
  // thermostat whose circuits sit in two rooms is reported to the hub under
  // the first of them, which is right for the hub and wrong for a line that
  // counts what the resident has placed: the second room would still read as
  // missing after they had answered for it. Duplicates are fine, the line
  // counts a room once.
  const mappedRoomNames = allCircuits.map(roomOf).filter((room) => room && room !== OUTSIDE_PILOT);
  const roomsLine = roomsToMapLine(recordRoomNames, mappedRoomNames);
  const roomsLeft = recordRoomNames.filter((r) => !mappedRoomNames.includes(r)).length;
  // One thermostat, two or more record rooms: the Wavin holds one temperature
  // for all of them, so they cannot stay separate rooms. Carried in the step
  // payload and named on the summary; the confirm-and-merge step builds on it.
  const sharedThermostats = circuits
    .map((c) => ({ zone: c.id, circuits: c.circuits, rooms: zoneRooms(c) }))
    .filter((c) => c.rooms.length > 1);
  // Rooms to list on the summary: the record's rooms, plus any room the mapping
  // names that the record does not carry yet. Right after a merge the mapping
  // already says "Kitchen and Living Room" while the rooms prop refreshes.
  const summaryRooms = [
    ...roomNames,
    ...assigned
      .map((a) => a.room)
      .filter((r, i, all) => !roomNames.includes(r) && all.indexOf(r) === i),
  ];

  /**
   * Push the mapping to SmartClimate, then show the summary.
   *
   * Saving is the list screen's action now, so this no longer completes the
   * module: the summary screen confirms what landed and completes from there.
   * A user who saves, sees an unmatched circuit and goes back can fix it and
   * save again.
   */
  async function saveMapping() {
    setApplyError("");
    // Already pushed, and the user has now seen which circuits didn't match.
    if (appliedResult) {
      setStage("done");
      return;
    }
    // Every thermostat placed outside the pilot, or nothing placed at all, is
    // a legitimate answer with nothing to tell the hub. Calling it with an
    // empty list would come back "none linked" and read as a failure.
    if (assigned.length === 0) {
      setStage("done");
      return;
    }
    try {
      // Push the mapping into Home Assistant (areas + entity renames) and the
      // household record, then complete the step with the applied result.
      const result = await applyMapping.mutateAsync(
        assigned.map((a) => ({ zone: a.zone, room_name: a.room })),
      );
      logger.debug("[onboarding] heating mapping applied", result);
      // The hub matches each circuit to a room by NAME, so a room the
      // household record doesn't carry silently maps nothing. Reporting
      // success then would leave the resident believing their heating is set
      // up when the hub never learned a thing.
      if (result && result.linked.length === 0) {
        setApplyError(
          `None of the circuits could be matched to a room in your home (${result.unmatched.join(", ")}). Nothing was saved. We have noted it and VELUX will reach out.`,
        );
        return;
      }
      if (result && result.unmatched.length > 0) {
        // Partly applied: what matched IS saved, so hold the user once to show
        // the gaps rather than blocking a retry that can't fix them.
        setAppliedResult(result);
        setApplyError(
          `Saved ${result.linked.length} of ${assigned.length} thermostats. These couldn't be matched to a room: ${result.unmatched.join(", ")}. Tap continue again to move on.`,
        );
        return;
      }
      // A thermostat placed in two record rooms is the open-plan merge case.
      // The hub now holds the survivor's mapping; the resident confirms the
      // merge before the record follows.
      if (sharedThermostats.length > 0) {
        setMergeQueue(sharedThermostats);
        setMergeError("");
        setStage("confirm-merge");
      } else {
        setStage("done");
      }
    } catch (err) {
      setApplyError(getApiErrorMessage(err, "Could not save the mapping to SmartClimate."));
    }
  }

  /** Merge the first queued thermostat's rooms, then move on or finish. */
  async function confirmMerge() {
    const shared = mergeQueue[0];
    if (!shared) {
      setStage("done");
      return;
    }
    setMergeError("");
    try {
      const result = await mergeRooms.mutateAsync({ zone: shared.zone, room_names: shared.rooms });
      logger.debug("[onboarding] rooms merged", result);
      // The circuits now belong to one room, so the summary reads that way
      // and the group is no longer "shared".
      if (result) {
        setMapping((prev) => {
          const next = { ...prev };
          for (const n of shared.circuits) {
            if (next[n] && next[n] !== OUTSIDE_PILOT) {
              next[n] = result.merged_room;
            }
          }
          return next;
        });
      }
      // A later thermostat may name a room this merge just absorbed; the
      // backend only knows live rooms, so those names become the merged one.
      // A group left with a single room is no longer shared and drops out.
      const absorbedNames = new Set(shared.rooms);
      const mergedName = result?.merged_room ?? shared.rooms.join(" and ");
      const rest = mergeQueue
        .slice(1)
        .map((item) => ({
          ...item,
          rooms: [...new Set(item.rooms.map((r) => (absorbedNames.has(r) ? mergedName : r)))],
        }))
        .filter((item) => item.rooms.length > 1);
      setMergeQueue(rest);
      if (rest.length === 0) {
        setStage("done");
      }
    } catch (err) {
      setMergeError(getApiErrorMessage(err, "Could not merge the rooms."));
    }
  }

  // ── Stage 1: intro — "Setting up Heating" + the kit parts (Figma 1065:1451)
  if (stage === "intro") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="floor_heating" stage={stage} title={PRODUCT_NAMES.heating} />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          {/* Flat module block instead of the soft wash artwork that used to
              sit behind the unit — the tint is what says "Heating" now. */}
          <div
            className={`relative flex h-[183px] shrink-0 overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <img
              src="/brand/onboarding/heating-wavin.png"
              alt="The Wavin floor-heating unit"
              className="relative mx-auto h-full w-auto object-contain px-4 py-1"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Setting up Heating</h1>
            <p className="text-muted text-[17px] leading-6">
              <span className="font-bold">Why this part?</span> Your Wavin already heats each room
              separately, but you set it by hand. Once it&apos;s connected, SmartClimate adjusts
              each room for you, using the data from your air sensors.
            </p>
          </div>
          <MaterialsNeeded
            items={[
              {
                title: HEATING_CONNECTOR,
                caption: "To connect with your Wavin floor heating. Includes antenna.",
                img: "/brand/onboarding/heating-connector.png",
                img2: "/brand/onboarding/heating-antenna.png",
                alt: `The ${HEATING_CONNECTOR}`,
              },
              {
                title: "Yellow cable",
                caption: "RS485 to ethernet cable",
                img: "/brand/onboarding/heating-cable.png",
                alt: "The yellow RS485-to-ethernet cable",
              },
              {
                title: "Power adapter",
                caption: `For the ${HEATING_CONNECTOR}.`,
                img: "/brand/onboarding/heating-adapter.png",
                alt: "The power adapter",
              },
            ]}
          />
          <p className="text-primary text-[17px] leading-6">
            Make sure you have everything ready before we start.
          </p>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="I have everything" onClick={() => setStage("connect")} />
        </div>
      </div>
    );
  }

  // ── Stage 2: connect — plug the connector into the Wavin (Figma 717:940) ─
  if (stage === "connect") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="floor_heating"
          stage={stage}
          title={PRODUCT_NAMES.heating}
          onBack={() => setStage("intro")}
        />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-[18px] px-6 pt-2">
          <div
            className={`relative flex h-[183px] shrink-0 overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <img
              src="/brand/onboarding/heating-connect-art.png"
              alt={`The ${HEATING_CONNECTOR} wired to the Wavin unit with the yellow cable`}
              className="relative mx-auto h-full w-auto object-contain px-3 py-2"
            />
          </div>
          <div className="flex flex-col gap-2">
            <h1 className="text-primary type-headline">
              Wire the {HEATING_CONNECTOR} to your Wavin
            </h1>
            <p className="text-muted text-[17px] leading-6">
              Take compartment 4 to your Wavin unit. It&apos;s usually in a utility room, a
              cupboard, or under the stairs.
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            {[
              // "Hand-tight is enough" was written to stop anyone reaching for
              // a spanner, and it worked too well: an antenna can look attached
              // while still a turn short, and a loose one made a real install
              // crawl. Both halves have to be said.
              "Screw the antenna on until it stops turning. Finger-tight, but all the way in.",
              `Plug the cable's green end into the green port on the ${HEATING_CONNECTOR}.`,
              "Plug the other end into the port on the bottom of your Wavin.",
              `Plug the ${HEATING_CONNECTOR} into your power strip.`,
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="bg-background flex size-6 shrink-0 items-center justify-center rounded-xl">
                  <span className="text-primary type-caption">{i + 1}</span>
                </div>
                <p className="text-muted text-[15px] leading-[22px]">{step}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-3.5 pb-6">
          {provisionFailed && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {provision.data?.status === "error"
                ? (provision.data.detail ?? "The gateway could not be registered.")
                : getApiErrorMessage(provision.error, "The gateway could not be registered.")}
            </p>
          )}
          <PillButton
            label={provisionFailed ? "Try again" : "The power light is on"}
            onClick={startConnecting}
          />
          {provisionFailed && (
            <StuckHelp
              stepKey="floor_heating"
              stage="provision_failed"
              lastError={
                provision.data?.status === "error"
                  ? (provision.data.detail ?? "The gateway could not be registered.")
                  : getApiErrorMessage(provision.error, "The gateway could not be registered.")
              }
              seed={`Registering the ${HEATING_CONNECTOR} on my hub failed. What went wrong and what should I do?`}
              label="Ask what went wrong"
            />
          )}
          {provisionFailed && onSkip && (
            <SkipFailedStep
              busy={skipping}
              onSkip={() => {
                const reason =
                  provision.data?.status === "error"
                    ? (provision.data.detail ?? "The gateway could not be registered.")
                    : getApiErrorMessage(provision.error, "The gateway could not be registered.");
                recordConnectorIssue("provision_failed", reason);
                onSkip(reason);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Stage 3: connecting — provision + wait for the gateway and zones ─────
  if (stage === "connecting") {
    // What the screen knows about why this is still going: the verdict if
    // one has landed, the hub's own words for the entry if not. Hoisted so
    // the caption and the assistant read from the same place, and so the
    // reassurance below (which is not an error) never travels as one.
    const connectingReason = activeDiagnosis?.detail ?? entry?.reason ?? undefined;
    const captionText =
      connectingReason ??
      "This can take up to fifteen minutes. Keep the app open and leave everything plugged in.";
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="floor_heating"
          stage={stage}
          title={PRODUCT_NAMES.heating}
          onBack={() => setStage("connect")}
        />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col items-center gap-10 px-6 py-10">
          {/* Once the verdict says nobody in the house can bring this
              connector up, the hero stops pretending to wait: the ring stops
              pulsing and the Wi-Fi glyph becomes the red cross the rest of
              the flow uses for a step that did not happen. A fixable verdict
              keeps the pulse, because there the connector may still come up
              once the resident has acted. */}
          <div className="relative flex size-[180px] items-center justify-center">
            <div
              className={cn(
                "bg-accent-light/40 absolute inset-0 rounded-full",
                !escalating && "animate-ping",
              )}
            />
            <div className="bg-accent-light/60 absolute inset-5 rounded-full" />
            <div className="border-border bg-background relative flex size-20 items-center justify-center rounded-[40px] border">
              {escalating ? (
                <X className="text-destructive size-10" strokeWidth={2.5} aria-hidden />
              ) : (
                <Wifi className="text-accent size-10" strokeWidth={2.5} />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3 text-center">
            {/* The title stops saying "connecting" the moment the verdict says
                nobody in the house can finish it. The subtitle names the
                outcome once and points at the notice rather than repeating it. */}
            {escalating ? (
              <>
                <h1 className="text-primary type-headline">We could not connect to your Wavin</h1>
                <p className="text-muted text-[17px] leading-6">
                  The {HEATING_CONNECTOR} is registered, but your hub cannot reach it. The details
                  are below.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-primary type-headline">Connecting to your Wavin…</h1>
                <p className="text-muted text-[17px] leading-6">
                  We&apos;re registering the {HEATING_CONNECTOR} and reading the circuits your Wavin
                  controls.
                </p>
              </>
            )}
          </div>
          <div className="flex w-full flex-col gap-4 px-5">
            {CONNECT_STEPS.map((label, i) => {
              const done = i < connectStep;
              const active = i === connectStep;
              return (
                <div key={label} className="flex items-center gap-3">
                  {done ? (
                    <Check className="text-success size-5 shrink-0" strokeWidth={3} />
                  ) : active && escalating ? (
                    <X className="text-destructive size-5 shrink-0" strokeWidth={3} aria-hidden />
                  ) : active ? (
                    <Loader2 className="text-accent size-5 shrink-0 animate-spin" />
                  ) : (
                    <Circle className="text-border size-5 shrink-0" />
                  )}
                  <p
                    className={cn(
                      "text-[17px] leading-6",
                      done || active ? "text-primary" : "text-muted",
                    )}
                  >
                    {active && !escalating ? `${label}…` : label}
                  </p>
                </div>
              );
            })}
          </div>
          {readyToContinue ? (
            <p className="text-muted type-body-sm text-center">
              Your Wavin is connected. Tap continue to map your circuits.
            </p>
          ) : (
            <>
              {/* The ceiling, not the average. A gateway that takes fifteen
                  minutes is normal; "under a minute" turned that into
                  fourteen minutes of evidence that something had broken, and
                  the only lever a resident has at that point is the power
                  cable. Pulling it mid-provision is the one thing that
                  actually does damage.

                  The whole line goes once a notice is up, not just its
                  verdict: everything this caption can fall back to contradicts
                  the notice. The hub's own reason is a raw Home Assistant
                  string, and the reassurance is an invitation to keep waiting
                  for a connector we have just said nobody in the house can
                  bring up. */}
              {!noticeDiagnosis && (
                <p className="text-muted type-body-sm text-center">{captionText}</p>
              )}
              {/* A verdict outranks both timers. The notice carries its own
                  action, so the generic prompt stands down rather than
                  offering a second, vaguer way into the same assistant. */}
              {noticeDiagnosis ? (
                <DiagnosisNotice
                  diagnosis={noticeDiagnosis}
                  stepKey="floor_heating"
                  stage="connecting"
                  connectorName={HEATING_CONNECTOR}
                  busy={skipping}
                  onContinue={onSkip && (() => continueWithoutConnector())}
                />
              ) : (
                <StuckHelp
                  stepKey="floor_heating"
                  stage="connecting"
                  afterMs={CONNECT_PATIENCE_MS}
                  lastError={connectingReason}
                  seed={`I'm waiting for the ${HEATING_CONNECTOR} to connect and it's taking a long time. The antenna is screwed all the way in. What should I check?`}
                  label="Still waiting? Get help"
                  hasFailed={retryState === "failed"}
                  escalateLabel="We've noticed an issue"
                  escalateSeed={`The ${HEATING_CONNECTOR} still hasn't connected after several tries. What should I check?`}
                />
              )}
              {/* Same hang as ventilation's connecting stage: a connector the
                  hub cannot discover never resolves either way, so the wait
                  needs its own way out. Hidden while an escalate verdict is up:
                  the notice already offers the way on, and two skips on one
                  screen is a choice between identical doors. Hidden rather than
                  unmounted so a later poll that flips the verdict back to an
                  on-site fault hands back a timer that has kept running. */}
              {onSkip && (
                <SkipFailedStep
                  busy={skipping}
                  afterMs={SKIP_PATIENCE_MS}
                  hidden={escalating}
                  onSkip={() => continueWithoutConnector()}
                />
              )}
            </>
          )}
        </div>
        {readyToContinue && (
          <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
            <PillButton label="Continue" onClick={() => setStage("review")} />
          </div>
        )}
      </div>
    );
  }

  // ── MAC mismatch · the hub found a different device than we expected ─────
  if (stage === "mac-mismatch") {
    const answering = recordIssue.isPending || skipping;
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="floor_heating" stage={stage} title={PRODUCT_NAMES.heating} />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-6 p-6">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              Check the label on your {HEATING_CONNECTOR}
            </h1>
            <p className="text-muted text-[17px] leading-6">
              {mismatchDiagnosis?.question ??
                `The connector we have on record for your home does not match the one your hub found. Look at the label on the back of the ${HEATING_CONNECTOR}.`}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <img
              src="/brand/onboarding/connector-mac-label.jpg"
              alt={`The label on the back of the ${HEATING_CONNECTOR}, with the MAC address marked`}
              className="w-full rounded-2xl"
            />
            {/* The label's own Serial Param line reads 57600/N/8/1, the
                factory default rather than what is configured. Left
                unexplained, that line misleads anyone using this same photo
                to debug a serial mismatch later. */}
            <p className="text-muted type-body-sm">
              The Serial Param line on the label is a factory default, not what your connector is
              set to. Read the MAC line only.
            </p>
          </div>
        </div>
        {onSkip && (
          <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
            <PillButton
              label="Yes, it matches"
              busy={answering}
              onClick={() => answerMacMismatch(true)}
            />
            <PillButton
              label="No, it doesn't match"
              variant="secondary"
              withArrow={false}
              busy={answering}
              onClick={() => answerMacMismatch(false)}
            />
          </div>
        )}
      </div>
    );
  }

  // ── No circuits · the hub is up but the Wavin reported nothing ───────────
  if (stage === "no-circuits") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="floor_heating" stage={stage} title={PRODUCT_NAMES.heating} />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-6 p-6">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">No circuits found</h1>
            {/* Says what happened, then what it means for them. The connector
                working is worth stating: it is the part they did, and it did
                land, so the failure is not theirs to feel bad about. */}
            <p className="text-muted text-[17px] leading-6">
              The {HEATING_CONNECTOR} is connected and your hub is reading it. The Wavin behind it
              reported no circuits, so there is nothing to match to your rooms yet.
            </p>
            <p className="text-muted text-[17px] leading-6">
              That points at the wiring between the Wavin and the connector, or at setup still
              missing on the hub. Neither is something to fix from your phone.
            </p>
          </div>
          <div className="mt-auto flex flex-col gap-3">
            <StuckHelp
              stepKey="floor_heating"
              stage="no-circuits"
              lastError="The gateway loaded but the hub reported zero heating zones."
              seed={`My ${HEATING_CONNECTOR} is connected but the app says no circuits were found. What should I check on the Wavin?`}
              label="Get help with this"
            />
            {/* No patience gate here. The connecting stage waits because a
                provision legitimately takes time; this is a settled answer, so
                holding the way out back would only waste the resident's time. */}
            {onSkip && (
              <SkipFailedStep
                busy={skipping}
                onSkip={() => {
                  const reason =
                    "The Heating Connector came online but the Wavin reported no circuits.";
                  // No gateway diagnosis here: the connector reached the hub
                  // fine, so there is nothing for it to have diagnosed.
                  reportIssue({
                    step_key: "floor_heating",
                    stage: "no-circuits",
                    detail: "The gateway loaded but the hub reported zero heating zones.",
                    what_was_tried:
                      "The gateway connected. The Wavin reported no circuits after the usual wait.",
                  });
                  onSkip(reason);
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Review · the circuit list, read off the Wavin and edited here ────────
  if (stage === "review") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="floor_heating" stage={stage} title={PRODUCT_NAMES.heating} />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-6 p-6">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Map your circuits</h1>
            {/* The sentence that does the work. Danish installations must carry
                this list on the unit, so most households are being told the
                answer is already in front of them. That is why this screen is
                the destination and the room-by-room walk is the fallback.
                "Usually" is deliberate: plenty of units have it somewhere else,
                and someone whose panel is on the cover or in a folder should
                not read this and think they have the wrong thing. */}
            <p className="text-muted text-[17px] leading-6">
              Your Wavin has the same list, usually in a Channel Overview panel on its right-hand
              side. Match each number to the room it heats.
            </p>
            {/* One thermostat can drive several circuits, and the unit reports
                the group under its lowest number. Without this line the missing
                numbers read as a bug ("where did 7 and 8 go?"). */}
            {hasGroups && (
              <p className="text-muted type-body-sm">
                Circuits in one block share a thermostat. They are kept at one temperature whatever
                rooms they heat.
              </p>
            )}
          </div>
          <HelpHint
            question={`What does "${OUTSIDE_PILOT_LABEL}" mean?`}
            answer="Pick it for a circuit that heats a room you are not setting up with SmartClimate, like a hallway or a spare bedroom. That room keeps heating exactly as it does today. SmartClimate just won't read or adjust it."
          />
          <img
            src="/brand/onboarding/heating-circuit-list.png"
            alt="A Wavin unit with a Channel Overview panel listing a room name beside each numbered channel"
            className="w-full rounded-2xl"
          />
          {/* What the sheet owes, by name, from the household record, sitting
              on top of the list it is counting. Twelve numbered circuits is an
              abstract list and a resident who cannot place the first one has no
              idea how much is being asked. Four rooms is a short errand with an
              end, and it shrinks as they go. */}
          {roomsLine && (
            <p
              className={cn(
                "type-body-sm rounded-md px-3 py-2",
                roomsLeft === 0 ? "bg-success/10 text-success" : "bg-surface-subtle text-primary",
              )}
            >
              {roomsLine}
            </p>
          )}
          <div className="flex flex-col gap-3">
            {circuits.map((c) => {
              const select = (n: number) => (
                <select
                  value={roomOf(n)}
                  onChange={(e) => assignRoom(n, e.target.value)}
                  aria-label={`Room for circuit ${n}`}
                  className={cn(
                    "min-w-0 flex-1 appearance-none bg-transparent text-[17px] leading-6 focus:outline-none",
                    roomOf(n) && roomOf(n) !== OUTSIDE_PILOT ? "text-primary" : "text-muted",
                  )}
                >
                  <option value="">Not connected</option>
                  <option value={OUTSIDE_PILOT}>{OUTSIDE_PILOT_LABEL}</option>
                  {roomNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              );
              if (c.circuits.length === 1) {
                return (
                  <label
                    key={c.id}
                    className="border-border flex items-center gap-3 rounded-2xl border p-4"
                  >
                    <span className="bg-background text-primary flex size-8 shrink-0 items-center justify-center rounded-lg text-[17px] leading-6">
                      {c.id}
                    </span>
                    <ArrowRight className="text-faint size-3.5 shrink-0" />
                    {select(c.id)}
                    <Pencil className="text-faint size-5 shrink-0" />
                  </label>
                );
              }
              // A thermostat driving several circuits is one block: the numbers
              // stay on the sheet's numbering, the resident places each one, and
              // the block says why they sit together. The lowest circuit is the
              // one the hub names the thermostat after, so it is the dark chip.
              return (
                <div key={c.id} className="border-border overflow-hidden rounded-2xl border">
                  <p className="bg-background text-muted type-label flex items-center gap-2 px-4 py-2">
                    <span className="bg-accent size-2 shrink-0 rounded-full" aria-hidden />
                    One thermostat · {spellCountLower(c.circuits.length)} circuits
                  </p>
                  {c.circuits.map((n, i) => (
                    <label
                      key={n}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3",
                        i > 0 && "border-border border-t",
                      )}
                    >
                      <span
                        className={cn(
                          "flex size-8 shrink-0 items-center justify-center rounded-lg text-[17px] leading-6",
                          n === c.id
                            ? "bg-accent text-white"
                            : "border-border text-primary border bg-white",
                        )}
                      >
                        {n}
                      </span>
                      <ArrowRight className="text-faint size-3.5 shrink-0" />
                      {select(n)}
                      <Pencil className="text-faint size-5 shrink-0" />
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
          <p className="text-muted type-body-sm">
            Leave any unused circuit on &quot;Not connected&quot;. A room we are not tracking still
            heats as it does today; SmartClimate just won&apos;t manage it. You can change these
            later.
          </p>
          {/* Deliberately the last thing on the page, and deliberately quiet.
              Firing a circuit to see which floor warms up is slow and we would
              rather nobody took it before reading their own list. */}
          <PillButton
            variant="secondary"
            label="Don't know? Find out room by room"
            onClick={() => {
              setRoomIdx(0);
              setConfirmed([]);
              setPendingHits([]);
              setBaseline(null);
              setSawSetpointHit(false);
              setAppliedResult(null);
              setStage("fire-intro");
            }}
          />
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-5">
          {(applyError || error) && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {applyError || error}
            </p>
          )}
          {/* Nothing here until the sheet says something. The mapping IS the
              step, and the button that used to sit here saved nothing while
              saying "Save": Beta Test House 13 pressed it with twelve circuits
              untouched and skipped the step two screens later. */}
          {mayContinue && (
            <PillButton
              label="Save and continue"
              busy={busy || applyMapping.isPending}
              onClick={() => void saveMapping()}
            />
          )}
          {/* Only once saving has actually failed, and only when nothing landed:
              a partly-applied mapping is progress worth keeping, and its own
              copy already tells them to tap continue again. */}
          {applyError && !appliedResult && onSkip && (
            <SkipFailedStep busy={skipping} onSkip={() => skipUnsavedMapping()} />
          )}
        </div>
      </div>
    );
  }

  // ── Fallback · fire-intro — how the room-by-room detection works ─────────
  if (stage === "fire-intro") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="floor_heating"
          stage={stage}
          title={PRODUCT_NAMES.heating}
          onBack={() => setStage("review")}
        />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-5 px-6 pt-2">
          <div className={`flex h-[187px] items-center justify-center rounded-2xl ${MODULE_HERO}`}>
            <img
              src="/brand/onboarding/thermostat.png"
              alt="A room thermostat"
              className="h-[167px] object-contain"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">We&apos;ll find them one room at a time</h1>
            <p className="text-muted text-[17px] leading-6">
              Turn a thermostat up by 6°C and its circuit starts heating. We watch which one moves,
              and that&apos;s the room mapped.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-muted type-label">How it works</p>
            <div className="flex items-start gap-3">
              {[
                { icon: Thermometer, label: "You raise the thermostat" },
                { icon: Zap, label: "Circuit fires" },
                { icon: Tag, label: "We name it" },
              ].map((item, i) => (
                <div key={item.label} className="flex flex-1 items-start gap-3">
                  <div className="flex flex-1 flex-col items-center gap-2.5">
                    <div className="border-border bg-background flex size-12 items-center justify-center rounded-xl border">
                      <item.icon className="text-primary size-5.5" />
                    </div>
                    <p className="text-primary type-caption text-center">{item.label}</p>
                  </div>
                  {i < 2 && <ArrowRight className="text-faint mt-3.5 size-3.5 shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton
            label={`Start with the ${roomNames[0] ?? "first room"}`}
            onClick={() => setStage("fire-raised")}
          />
        </div>
      </div>
    );
  }

  // ── Fallback · fire-raised — send the user to the next room ──────────────
  if (stage === "fire-raised") {
    const roomName = roomNames[roomIdx] ?? roomNames[0]!;
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="floor_heating"
          stage={stage}
          title={PRODUCT_NAMES.heating}
          onBack={() => setStage(roomIdx === 0 ? "fire-intro" : "review")}
        />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-6 px-6 pt-2">
          <div
            className={`relative flex h-[187px] items-center justify-center rounded-2xl ${MODULE_HERO}`}
          >
            <img
              src="/brand/onboarding/thermostat.png"
              alt="A room thermostat"
              className="h-[160px] object-contain"
            />
            <div className="bg-accent absolute top-1/2 left-[calc(50%+40px)] flex size-12 -translate-y-5 items-center justify-center rounded-3xl shadow-[0px_4px_4px_rgba(0,0,0,0.15)]">
              <ArrowRight className="size-6 -rotate-90 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Go to the {roomName}</h1>
            <p className="text-muted text-[17px] leading-6">
              Turn the thermostat up by at least 6°C. Leave it up while we watch.
            </p>
          </div>
          <p className="text-muted type-caption">
            Room {roomIdx + 1} of {roomNames.length}
          </p>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="I've turned it up" onClick={() => setStage("fire-listening")} />
        </div>
      </div>
    );
  }

  // ── Fallback · listening — watch live valve state for the fired circuit ──
  if (stage === "fire-listening") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="floor_heating"
          stage={stage}
          title={PRODUCT_NAMES.heating}
          onBack={() => setStage("fire-raised")}
        />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-6 px-6 pt-2">
          <div
            className={`relative flex h-[240px] items-center justify-center overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <div className="border-accent/30 absolute size-[180px] animate-ping rounded-full border" />
            <div className="border-accent/40 absolute size-[140px] rounded-full border" />
            <div className="border-accent/50 absolute size-[100px] rounded-full border" />
            <img
              src="/brand/onboarding/thermostat.png"
              alt="A room thermostat"
              className="relative h-[102px] object-contain"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Listening for your thermostat…</h1>
            <p className="text-muted text-[17px] leading-6">
              Your Wavin reports every 30 to 60 seconds, so this can take up to a minute. Keep the
              thermostat up.
            </p>
          </div>
          <div className="bg-background flex flex-col gap-3 rounded-2xl p-4">
            <p className="text-muted type-label">Listening</p>
            {/* Wraps because a full Wavin has sixteen circuits. A single
                justified row fit the old room-derived handful and silently
                overflowed once the real count arrived. */}
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-3">
              {circuits.map((c) => {
                const isConfirmed = confirmed.includes(c.id);
                const isHit = pendingHits.includes(c.id);
                const zone = zones.data?.find((z) => z.zone === c.id);
                const calling = zone?.valve_open === true || zone?.hvac_action === "heating";
                return (
                  <div key={c.id} className="flex flex-col items-center gap-1.5">
                    <div
                      className={cn(
                        "size-5 rounded-full",
                        isConfirmed
                          ? "bg-success"
                          : isHit || calling
                            ? "bg-accent animate-pulse"
                            : "border-border border bg-white",
                      )}
                    />
                    <p className="text-primary/85 type-caption">{c.id}</p>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="text-primary text-[14px]">
                {pendingHits.length > 0
                  ? `Circuit ${pendingHits.join(", ")} fired. Checking for more…`
                  : "Listening for active circuits…"}
              </p>
              <p className="text-muted type-body-sm">
                Keep the thermostat raised until the circuits light up.
              </p>
            </div>
          </div>
          <div className="flex justify-center">
            <button type="button" onClick={() => advanceRoom()} className="text-muted type-body-sm">
              Skip, this room has no floor heating
            </button>
          </div>
        </div>
        <div className="h-[60px] shrink-0" />
      </div>
    );
  }

  // ── Fallback · detected — confirm the fired circuit SET for this room ────
  if (stage === "fire-detected") {
    const hitList = [...pendingHits].sort((a, b) => a - b);
    const isLastRoom = roomIdx + 1 >= roomNames.length;
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="floor_heating"
          stage={stage}
          title={PRODUCT_NAMES.heating}
          onBack={() => setStage("fire-listening")}
        />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-6 px-6 pt-2">
          <div className={`flex h-[187px] items-center justify-center rounded-2xl ${MODULE_HERO}`}>
            <img
              src="/brand/onboarding/thermostat.png"
              alt="A room thermostat"
              className="h-[167px] object-contain"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              We found {hitList.length === 1 ? "it" : "them"}!
            </h1>
            <p className="text-muted text-[17px] leading-6">
              {hitList.length === 1
                ? `Circuit ${hitList[0]} fired.`
                : `Circuits ${hitList.join(", ")} fired together. This thermostat controls all of them.`}{" "}
              Which room were you just in?
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-muted type-label">Detected room</p>
            <div className="bg-background flex items-center justify-between rounded-2xl p-4">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-[20px] bg-white">
                  <Home className="text-primary size-5" />
                </div>
                <select
                  value={detectedRoom}
                  onChange={(e) => setDetectedRoom(e.target.value)}
                  aria-label={`Room for circuit${hitList.length === 1 ? "" : "s"} ${hitList.join(", ")}`}
                  className="text-primary min-w-0 flex-1 appearance-none bg-transparent text-[17px] leading-6 focus:outline-none"
                >
                  {roomNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <CheckCircle2 className="text-accent size-5 shrink-0" />
            </div>
            <div className="flex flex-wrap gap-2">
              {hitList.map((zone) => (
                <span
                  key={zone}
                  className="bg-surface-subtle text-primary type-caption rounded-[10px] px-3 py-2"
                >
                  Circuit {zone}
                </span>
              ))}
            </div>
            <p className="text-muted type-body-sm">
              Not right? You can rename or adjust after setup.
            </p>
          </div>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-8">
          <PillButton
            label={isLastRoom ? "Confirm and finish" : "Confirm and next room"}
            onClick={confirmDetected}
          />
        </div>
      </div>
    );
  }

  // ── Stage: confirm-merge — one thermostat heats two record rooms ─────────
  if (stage === "confirm-merge") {
    const shared = mergeQueue[0];
    if (!shared) {
      // The effect above moves on to the summary; render nothing meanwhile.
      return null;
    }
    const mergedName = shared.rooms.join(" and ");
    const area = rooms
      .filter((r) => shared.rooms.includes(r.name))
      .reduce((sum, r) => sum + (r.area_m2 ?? 0), 0);
    const survivor = shared.rooms[0];
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="floor_heating"
          stage={stage}
          title={PRODUCT_NAMES.heating}
          onBack={() => setStage("review")}
        />
        <PhaseDots total={6} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-5 px-6 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              One thermostat heats {spellCountLower(shared.rooms.length)} of your rooms
            </h1>
            {/* No sensor talk here: that belongs to the air sensors closing task,
                where the resident is holding the second unit. */}
            <p className="text-muted text-[17px] leading-6">
              Circuits {joinNumbers(shared.circuits)} share a thermostat, and you placed them in{" "}
              {shared.rooms.join(" and ")}. The Wavin holds one temperature for all of them, so
              SmartClimate has to treat these as one room.
            </p>
          </div>
          <div className="border-border flex flex-col gap-3 rounded-2xl border-[1.5px] p-4">
            <div className="flex flex-wrap items-center gap-2 text-[17px]">
              {shared.rooms.map((name, i) => (
                <span key={name} className="flex items-center gap-2">
                  {i > 0 && <span className="text-muted">+</span>}
                  <span className="bg-surface-subtle text-primary rounded-lg px-2.5 py-1">
                    {name}
                  </span>
                </span>
              ))}
            </div>
            <p className="text-muted type-body-sm">
              Becomes <span className="text-primary">{mergedName}</span>
              {area > 0 && `, ${Math.round(area * 10) / 10} m²`}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {shared.circuits.map((n) => (
                <span
                  key={n}
                  className={cn(
                    "flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-[13px] leading-6",
                    n === shared.zone ? "bg-accent text-white" : "bg-background text-primary",
                  )}
                >
                  {n}
                </span>
              ))}
              <span className="text-muted type-caption ml-1">one thermostat</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-primary flex items-start gap-2 text-[15px] leading-[22px]">
              <Check className="text-success mt-0.5 size-4 shrink-0" strokeWidth={3} aria-hidden />
              The thermostat in the {survivor} sets the temperature for the whole space.
            </p>
            <p className="text-primary flex items-start gap-2 text-[15px] leading-[22px]">
              <Check className="text-success mt-0.5 size-4 shrink-0" strokeWidth={3} aria-hidden />
              Your other rooms are not affected.
            </p>
          </div>
          <p className="text-muted type-body-sm">
            If a wall separates these rooms, go back and check the circuit list. This is the step
            where a mistake is easy to catch.
          </p>
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-5">
          {mergeError && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {mergeError}
            </p>
          )}
          {/* Only once a join has failed. Before that the screen explains
              itself, and the help button in the bar covers the curious. */}
          {mergeError && (
            <StuckHelp
              stepKey="floor_heating"
              stage="confirm-merge"
              lastError={mergeError}
              seed={`The app says ${shared.rooms.join(" and ")} share a thermostat, and joining them into one room did not finish. What should I do?`}
              label="Ask what to do"
            />
          )}
          <PillButton
            label="Yes, treat them as one room"
            busy={mergeRooms.isPending}
            onClick={() => void confirmMerge()}
          />
          <PillButton
            variant="secondary"
            label="Back to the circuit list"
            onClick={() => setStage("review")}
          />
        </div>
      </div>
    );
  }

  // ── Stage: done — all circuits mapped summary ────────────────────────────
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
      <AppBar stepKey="floor_heating" stage={stage} title={PRODUCT_NAMES.heating} />
      <PhaseDots total={6} activeIndex={3} />
      <div className="flex flex-1 flex-col gap-5 px-6 pt-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-primary type-headline">
            {outcome === "empty" ? "Nothing is mapped yet" : "Your heating is mapped"}
          </h1>
          {/* Saved already, on the list. This screen reports a result rather
              than promising one, which is what the old "one more click applies
              the names" line did while its button said "Next step". */}
          <p className="text-muted text-[17px] leading-6">
            {outcome === "empty"
              ? `${
                  unassignedCount === allCircuits.length
                    ? "Every circuit is still on Not connected."
                    : `${spellCount(unassignedCount)} of your ${spellCountLower(allCircuits.length)} circuits are still on Not connected, and none has a room yet.`
                } Without the mapping, SmartClimate cannot adjust the floor heating in any of your rooms. Go back to the sheet and pick a room for each circuit you can identify. Mark the rest as a room we are not tracking.`
              : `${
                  assigned.length > 0
                    ? `${spellCount(assigned.length)} ${
                        assigned.length === 1 ? "thermostat is" : "thermostats are"
                      } mapped to your rooms. `
                    : "No thermostat is mapped to a room yet. "
                }SmartClimate adjusts each room's floor heating from its air sensor.`}
          </p>
        </div>
        {/* One row per room, the resident's own order. Circuits are chips so a
            three-circuit thermostat reads at a glance; the old two-column grid
            truncated it. A room with nothing mapped stays visible: it is the
            one mistake worth catching here, and "left unmapped" alone would not
            say which room it was. */}
        <div className="flex flex-col gap-2">
          {summaryRooms.map((name) => {
            const mine = assigned.filter((a) => a.room === name);
            const nums = mine.flatMap((a) => a.circuits);
            return (
              <div
                key={name}
                className="bg-background flex items-center justify-between gap-3 rounded-2xl px-4 py-3"
              >
                <span className="text-primary min-w-0 flex-1 truncate text-[17px] leading-6">
                  {name}
                </span>
                {nums.length > 0 ? (
                  <span className="flex shrink-0 flex-wrap justify-end gap-1">
                    {nums.map((n) => (
                      <span
                        key={n}
                        className="border-border text-primary flex h-7 min-w-7 items-center justify-center rounded-md border bg-white px-1.5 text-[15px] leading-6"
                      >
                        {n}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="text-muted type-caption shrink-0">No circuit mapped</span>
                )}
              </div>
            );
          })}
        </div>
        {sharedThermostats.map((shared) => (
          <div key={shared.zone} className="bg-tint-rose flex gap-3 rounded-2xl p-4">
            <Thermometer className="text-primary size-5 shrink-0" aria-hidden />
            <p className="text-primary type-body-sm">
              Circuits {joinNumbers(shared.circuits)} share one thermostat and heat{" "}
              {shared.rooms.join(" and ")}. The Wavin keeps them at one temperature, so we will
              confirm with you how to treat them before your setup is handed over.
            </p>
          </div>
        ))}
        {(outsidePilotCircuits.length > 0 || unassignedCount > 0) && (
          <div className="flex flex-col gap-1">
            {outsidePilotCircuits.length > 0 && (
              <p className="text-muted type-body-sm">
                Not tracking {joinNumbers(outsidePilotCircuits)}.{" "}
                {outsidePilotCircuits.length === 1 ? "That room keeps" : "Those rooms keep"} heating
                as {outsidePilotCircuits.length === 1 ? "it does" : "they do"} today.
              </p>
            )}
            {unassignedCount > 0 && (
              <p className="text-muted type-body-sm">
                Left unmapped: {joinNumbers(allCircuits.filter((n) => !roomOf(n)))}.
              </p>
            )}
          </div>
        )}
      </div>
      <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-5">
        {error && (
          <p role="alert" className="text-destructive text-center text-[15px]">
            {error}
          </p>
        )}
        {/* An empty sheet does not hand in. The way forward is the sheet; the
            way out is the skip, which flags the house for support. */}
        {outcome === "empty" && (
          <PillButton label="Back to the sheet" onClick={() => setStage("review")} />
        )}
        {outcome === "empty" && onSkip && (
          <SkipFailedStep busy={skipping} onSkip={() => skipEmptyMapping()} />
        )}
        {outcome !== "empty" && (
          <PillButton
            label="See what's next"
            busy={busy}
            onClick={() =>
              onComplete({
                // One entry per circuit, so support can see the sheet as the
                // resident filled it in, not only what the hub was told.
                circuits: allCircuits.map((n) => {
                  const zone = circuits.find((c) => c.circuits.includes(n))?.id ?? n;
                  const room = roomOf(n);
                  return room === OUTSIDE_PILOT
                    ? { circuit_id: n, zone, room: null, outside_pilot: true }
                    : { circuit_id: n, zone, room: room || null };
                }),
                shared_thermostats: sharedThermostats,
                applied: appliedResult,
              })
            }
          />
        )}
      </div>
    </div>
  );
}
